import Label from 'components/Form/Label';
import { Description } from 'components/Form/Label/Description';
import React, { useId } from 'react';
import tw, { TwStyle } from 'twin.macro';

type Size = 'small' | 'big';

interface RadioProps {
  id: string;
  name?: string;
  label: React.ReactNode;
  description?: string;
  checked?: boolean;
  disabled?: boolean;
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  size?: Size;
}

const SIZE_STYLES: Record<
  Size,
  {
    radio: TwStyle;
    text: TwStyle;
    icon: TwStyle;
    container: TwStyle;
    spacing: TwStyle;
  }
> = {
  big: {
    radio: tw`size-5`,
    text: tw`text-base`,
    icon: tw`size-5`,
    container: tw`h-6`,
    spacing: tw`ml-2`,
  },
  small: {
    radio: tw`size-4`,
    text: tw`text-sm`,
    icon: tw`size-4`,
    container: tw`h-[22px]`,
    spacing: tw`ml-1.5`,
  },
};

export function Radio({
  id,
  name,
  label,
  description,
  checked,
  disabled,
  required,
  onChange,
  size = 'big',
  ...props
}: RadioProps) {
  const sizeStyles = SIZE_STYLES[size];
  const reactId = useId();
  const inputId = id ?? `radio-${reactId}`;

  return (
    <div
      css={[
        tw`relative flex items-start`,
        disabled && tw`opacity-40 cursor-not-allowed`,
      ]}
    >
      <div css={[tw`flex items-center relative`, sizeStyles.container]}>
        <input
          id={inputId}
          aria-describedby={id}
          aria-label={props['aria-label']}
          name={name}
          type="radio"
          role="radio"
          className="peer"
          tw="sr-only"
          checked={checked}
          disabled={disabled}
          required={required}
          onChange={onChange}
        />

        <span
          css={[
            tw`flex items-center justify-center rounded-full border bg-white shrink-0 transition-shadow peer-focus-visible:ring-2 peer-focus-visible:ring-brand-intense peer-focus-visible:ring-offset-2`,
            checked ? tw`border-brand-intense` : tw`border-neutral-moderate`,
            sizeStyles.icon,
          ]}
          aria-hidden
        >
          <span
            css={[
              tw`w-2.5 h-2.5 rounded-full`,
              checked ? tw`bg-brand-intense` : tw`bg-transparent`,
            ]}
          />
        </span>
      </div>
      {label && (
        <div css={sizeStyles.spacing}>
          <Label
            htmlFor={id}
            tw="cursor-pointer"
            css={[tw`cursor-pointer text-neutral-moderate`, sizeStyles.text]}
          >
            {label}
            {description && (
              <Description
                id={`${id}-description`}
                css={[tw`text-neutral-moderate font-normal`, sizeStyles.text]}
              >
                {description}
              </Description>
            )}
          </Label>
        </div>
      )}
    </div>
  );
}
